import React, { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, Search } from "lucide-react";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

const STATUSES = [
  { key: "not_order", label: "Not Order", active: "bg-rose-600 text-white", idle: "text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40", dot: "bg-rose-500" },
  { key: "ordering", label: "Ordering", active: "bg-amber-500 text-white", idle: "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/40", dot: "bg-amber-500" },
  { key: "arrived", label: "Arrived", active: "bg-emerald-600 text-white", idle: "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40", dot: "bg-emerald-500" },
];

export default function OutsourceOrders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/outsource-orders").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 20000);

  const setStatus = async (id, component, status) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, components: r.components.map((c) => (c.name === component ? { ...c, status } : c)) } : r)));
    try {
      await api.patch(`/outsource-orders/${id}`, { component, status });
      toast.success(`${component} → ${STATUSES.find((s) => s.key === status).label}`);
      load();
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const allComps = rows.flatMap((r) => r.components || []);
  const counts = STATUSES.map((s) => ({ ...s, n: allComps.filter((c) => c.status === s.key).length }));

  const ql = q.trim().toLowerCase();
  const filtered = rows
    .filter((r) => !ql || `${r.job_number} ${r.customer_name || ""} ${r.agent_name || ""}`.toLowerCase().includes(ql))
    .map((r) => (filter === "all" ? r : { ...r, components: (r.components || []).filter((c) => c.status === filter) }))
    .filter((r) => (r.components || []).length > 0);

  return (
    <div className="space-y-4" data-testid="outsource-page">
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">Stock &amp; Import</div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Truck className="w-6 h-6" /> Plate/Stamping Order</h1>
        <p className="text-sm text-muted-foreground">Hotstamping / Nameplate ordering status for all Wedding Album orders. Components follow the option selected on each order.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {counts.map((s) => (
          <Card key={s.key} className="p-4 flex items-center gap-3" data-testid={`outsource-count-${s.key}`}>
            <span className={`w-3 h-3 rounded-full ${s.dot}`} />
            <div><div className="text-2xl font-bold">{s.n}</div><div className="text-xs text-muted-foreground">{s.label}</div></div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job # or customer…" className="pl-8" data-testid="outsource-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="outsource-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground">
            <tr className="text-left">
              <th className="p-3">Job #</th>
              <th className="p-3">Customer / Agent</th>
              <th className="p-3">Products</th>
              <th className="p-3">Order Date</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody data-testid="outsource-rows">
            {filtered.map((r) => (
              <tr key={r.id} className="border-t align-top" data-testid={`outsource-row-${r.id}`}>
                <td className="p-3 font-mono font-medium">{r.job_number}</td>
                <td className="p-3">
                  {r.customer_name || "—"}
                  {r.agent_name && <Badge variant="outline" className="ml-1 text-[10px]">Agent: {r.agent_name}</Badge>}
                </td>
                <td className="p-3">{(r.products || []).join(", ")}</td>
                <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{formatDateTime(r.created_at)}</td>
                <td className="p-3">
                  <div className="space-y-1.5">
                    {(r.components || []).map((c) => (
                      <div key={c.name} className="flex items-center gap-2" data-testid={`outsource-comp-${r.id}-${c.name}`}>
                        <span className="w-24 text-xs font-semibold shrink-0">{c.name}</span>
                        <div className="inline-flex rounded-lg border p-0.5 bg-background" data-testid={`outsource-ctrl-${r.id}-${c.name}`}>
                          {STATUSES.map((s) => {
                            const on = c.status === s.key;
                            return (
                              <button
                                key={s.key}
                                onClick={() => setStatus(r.id, c.name, s.key)}
                                data-testid={`outsource-${s.key}-${r.id}-${c.name}`}
                                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${on ? s.active : s.idle}`}
                              >
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {r.outsource_updated_by && <div className="text-[10px] text-muted-foreground mt-1">Updated by {r.outsource_updated_by}</div>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-muted-foreground" data-testid="outsource-empty">{rows.length === 0 ? "No Wedding Album orders yet. They appear here automatically when an order includes a Wedding Album." : "No orders match your search or filter."}</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
